import { makeStyles } from '@material-ui/core/styles'
import dayjs from 'dayjs'
import { get, isEmpty, set } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'

import { getConfigValue } from 'api'
import BaseForm from 'app/components/BaseForm'
import { commonAttributes, programTypeList } from 'app/config'
import { convertArrayToObject } from 'app/helpers'
import { validateNotEmpty } from 'app/validators'

import { columns } from '../../components/terminationConfig'

const useStyles = makeStyles((theme) => ({
  formControl: {
    '& > *': {
      margin: theme.spacing(1)
    }
  },
  invoiceTextsContainer: {
    textAlign: 'center'
  },
  invoiceText: {
    textTransform: 'capitalize'
  },
  editor: {
    margin: theme.spacing(1),
    marginTop: theme.spacing(2),
    border: '1px solid #ccc',
    borderRadius: 8,
    padding: theme.spacing(1)
  }
}))

const headerTextMessage =
  '**Termination date is the new end date on which the enrolments need to be terminated.'

const errorInitialState = {
  isError: false,
  errorMessage: '',
  fieldErrors: {}
}

const TERMINATION_MONTH_HORIZON = 12

// Build the month options for terminating an Excel enrolment. Ops may only pick
// whole months, from the upcoming month onward - never the current month or an
// arbitrary date. Termination routes through the journey unsubscribe flow, so it
// is NOT bounded by this (monthly) enrolment's own end date; we offer the next
// 12 upcoming months. Each value is the 1st of the month (the backend keys off
// month/year and creates an UNSUBSCRIBE_REQUEST effective the 1st).
const buildTerminationMonthOptions = () => {
  const mappings = {}
  let cursor = dayjs().add(1, 'month').startOf('month')
  for (let i = 0; i < TERMINATION_MONTH_HORIZON; i++) {
    mappings[cursor.format('YYYY-MM-DD')] = cursor.format('MMMM YYYY')
    cursor = cursor.add(1, 'month')
  }
  return mappings
}

const EnrolmentForm = (props) => {
  const [terminationData, setTerminationData] = React.useState({
    reason: '',
    notes: ''
  })
  const [currentEditingData, setCurrentEditingData] = React.useState({})
  const [error, setError] = React.useState(errorInitialState)
  const [terminationReasons, setTerminationReasons] = useState([])
  const { isModalOpen, closeModal, onSaveClick, action, enrolmentData } = props

  useEffect(() => {
    ;(async () => {
      try {
        const reasons = await getConfigValue({
          key: 'ALFRED_TERMINATION_REASONS',
          app_name: 'alfred'
        })
        if (Array.isArray(reasons)) {
          reasons.push('Other')
          setTerminationReasons(reasons)
        }
      } catch (error) {
        console.error(error)
      }
    })()
  }, [])

  const modifiedColumns = React.useMemo(() => {
    let modifiedColumnsList = [
      {
        name: 'reason',
        label: 'Reason for termination',
        widget: 'select',
        validate: validateNotEmpty,
        valueLabelMappings: convertArrayToObject(terminationReasons)
      },
      ...columns
    ]

    if (
      enrolmentData?.type === 'program' ||
      programTypeList.includes(enrolmentData?.program?.type) ||
      programTypeList.includes(enrolmentData?.program_enrolment?.program?.type)
    ) {
      // Excel enrolments terminate at whole-month granularity, only from the
      // upcoming month onward (no arbitrary date, never the current month).
      // Other program types keep the free-form termination date picker.
      const isExcelEnrolment =
        enrolmentData?.program?.type === 'excel' ||
        enrolmentData?.program_enrolment?.program?.type === 'excel'

      const terminationDateColumn = isExcelEnrolment
        ? {
            name: 'end_date',
            label: 'Termination Month',
            widget: 'select',
            valueLabelMappings: buildTerminationMonthOptions(),
            validate: validateNotEmpty
          }
        : {
            ...commonAttributes.date,
            name: 'end_date',
            label: 'Termination Date',
            widget: 'datepicker',
            minDate: dayjs().format('YYYY-MM-DD'),
            maxDate: enrolmentData?.end_date,
            validate: validateNotEmpty
          }

      modifiedColumnsList = [terminationDateColumn, ...modifiedColumnsList]
    }

    return modifiedColumnsList
  }, [enrolmentData, terminationReasons])

  const handleFormData = (newFormData, modifiedColumns, elements) => {
    modifiedColumns.forEach((column) => {
      if (!column.disabledInForm) {
        const element = get(elements, column.formDataKey || column.name)
        if (element !== null && element !== undefined) {
          if (element instanceof HTMLElement) {
            if (element.type !== 'file') {
              set(newFormData, column.formDataKey || column.name, element.value)
            }
          } else {
            if (!isEmpty(column.resultStructure)) {
              if (column.widget === 'autocomplete') {
                set(
                  newFormData,
                  column.resultStructure.keyName,
                  typeof element === 'string'
                    ? element
                    : column.getFormDataValue(element)
                )
              } else {
                set(
                  newFormData,
                  column.resultStructure.keyName,
                  typeof element === 'string'
                    ? element
                    : get(element, column.resultStructure.keyValue)
                )
              }
            } else {
              set(newFormData, column.formDataKey || column.name, element)
            }
          }
        }
      }
    })
  }

  return (
    <>
      {action === 'terminate_enrolment' ? (
        <>
          <BaseForm
            formData={terminationData}
            setFormData={setTerminationData}
            onSaveClick={(val) => onSaveClick(val, action)}
            isModalOpen={isModalOpen}
            currentEditingData={currentEditingData}
            error={error}
            columns={modifiedColumns}
            handleFormData={handleFormData}
            handleModalClose={closeModal}
            headerText={headerTextMessage}
          />
        </>
      ) : null}
    </>
  )
}

EnrolmentForm.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  onSaveClick: PropTypes.func.isRequired,
  action: PropTypes.string.isRequired,
  courseBatches: PropTypes.object.isRequired,
  enrolmentData: PropTypes.object.isRequired
}

export default EnrolmentForm
